import { useRef, useState } from 'react';
import { BrandSelect } from '@/components/BrandSelect';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useActiveLocationId } from '@/lib/locationContext';
import { useSession } from '@/lib/auth';
import { randomUUID } from '@/lib/uuid';
import { isCell } from '@/lib/cell/config';
import { cellCall, cellGet, cellPost } from '@/lib/cell/api';

/**
 * Suggestion Box — the PRO'S FACE (Phase C of the redesign). Peter, 2026-08-06: "I need a real
 * one… the Hub is the external face." This is the board a pro sees: browse ideas ranked by votes,
 * raise their own (anonymous or named), upvote, comment, and watch status + IMAGE's official reply.
 *
 * ONE BOX, THREE SURFACES. Reads/writes the SAME spine (suggestion_ideas / _votes / _comments,
 * backend SUGG-1) the iOS app and IMAGE OS web read — so a vote here bumps the ranking on IMAGE's
 * console, and a status IMAGE sets there shows here. RLS scopes everything to the pro's own studio.
 *
 * IMAGE-posed prompts (suggestion_topics) are KEPT below the board — the pro still answers them.
 */

type Idea = {
  id: string; title: string; body: string | null; category: string | null; status: string;
  vote_count: number; pinned: boolean; author_name: string | null; is_anonymous: boolean; created_at: string;
};
type Comment = { id: string; idea_id: string; body: string; is_official: boolean; author_name: string | null; created_at: string };
// A prompt's answer type + (for a poll) its options. THE CONTRACT (must match image-os-web + iOS):
//   text → free text · yes_no → "yes"/"no" · single_choice → the chosen option string · rating_1_5 → "1".."5"
type Topic = { id: string; topic: string; description: string | null; question_type: string; options: string[] | null };

const CATEGORIES = ['Hours', 'Facilities', 'Perks', 'Marketing', 'Other'];
const STATUS: Record<string, { label: string; cls: string }> = {
  open: { label: 'Open', cls: 'bg-gray-100 text-gray-600' },
  reviewing: { label: 'Under review', cls: 'bg-blue-50 text-blue-700' },
  planned: { label: 'Planned', cls: 'bg-purple-50 text-purple-700' },
  in_progress: { label: 'In progress', cls: 'bg-amber-50 text-amber-700' },
  done: { label: 'Done', cls: 'bg-emerald-50 text-emerald-700' },
  declined: { label: 'Declined', cls: 'bg-red-50 text-red-700' },
};

export default function Suggestions() {
  const { activeLocationId } = useActiveLocationId();
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();

  // Resolve the pro's id + their active location once — both needed to submit/vote/comment.
  const { data: me } = useQuery<{ proId: string; name: string; locationId: string } | null>({
    queryKey: ['me-pro', userId, activeLocationId],
    enabled: !!userId,
    queryFn: async () => {
      if (isCell()) { // CELL (M28): GET /v1/pro-hub/users/{authUserId}/ideas-me — the pros read and, with no studio picked, the same current-membership fallback
        const { data: r } = await cellGet<{ pro: { id: string; first_name: string | null; last_name: string | null } | null; membership: { location_id: string } | null }>(
          `/v1/pro-hub/users/${userId}/ideas-me`, activeLocationId ? {} : { fallback: '1' });
        const p = r?.pro;
        if (!p) return null;
        const l = activeLocationId ?? r?.membership?.location_id ?? null;
        if (!l) return null;
        return { proId: p.id, name: [p.first_name, p.last_name].filter(Boolean).join(' ') || 'A pro', locationId: l };
      }
      // CELL (M28): the branch above answers on the cell build; below is the Supabase path, unchanged.
      const { data: pro } = await supabase.from('pros').select('id, first_name, last_name').eq('auth_user_id', userId!).maybeSingle();
      if (!pro) return null;
      let loc = activeLocationId ?? null;
      if (!loc) {
        // CURRENT memberships only. A closed membership (end_date set) is history, not a
        // studio the pro belongs to — Lisa Tester held an open row at BOTH sharon_zoho and
        // the retired sharon_ma, so this picked the dead studio and her idea landed where
        // no owner would ever see it. Prefer a membership that still has a live tenant row.
        // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
        const { data: pl } = await supabase.from('pro_locations').select('location_id, current_tenant_id')
          .eq('pro_id', pro.id)
          .or(`end_date.is.null,end_date.gte.${new Date().toISOString().slice(0, 10)}`)
          .order('current_tenant_id', { ascending: false, nullsFirst: false })
          .order('is_primary', { ascending: false })
          .limit(1).maybeSingle<{ location_id: string }>();
        loc = pl?.location_id ?? null;
      }
      if (!loc) return null;
      return { proId: pro.id, name: [pro.first_name, pro.last_name].filter(Boolean).join(' ') || 'A pro', locationId: loc };
    },
  });
  const locationId = me?.locationId;

  const { data: ideas, isLoading } = useQuery<Idea[]>({
    queryKey: ['sugg-ideas', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      // CELL (M28): GET /v1/pro-hub/locations/{locationId}/ideas (same select, filters and order)
      const { data, error } = isCell()
        ? await cellGet<{ rows: Idea[] }>(`/v1/pro-hub/locations/${locationId}/ideas`).then((r) => ({ data: r.data?.rows ?? null, error: r.error }))
        : await supabase.from('suggestion_ideas')
        .select('id, title, body, category, status, vote_count, pinned, author_name, is_anonymous, created_at')
        .eq('location_id', locationId!).is('merged_into', null)
        .order('pinned', { ascending: false }).order('vote_count', { ascending: false }).order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Idea[];
    },
  });

  const { data: myVotes } = useQuery<Set<string>>({
    queryKey: ['sugg-myvotes', me?.proId, locationId],
    enabled: !!me?.proId && !!locationId,
    queryFn: async () => {
      // CELL (M28): GET /v1/pro-hub/pros/{proId}/votes
      const { data } = isCell()
        ? { data: (await cellGet<{ rows: { idea_id: string }[] }>(`/v1/pro-hub/pros/${me!.proId}/votes`)).data?.rows ?? null }
        : await supabase.from('suggestion_votes').select('idea_id').eq('voter_pro_id', me!.proId);
      return new Set((data ?? []).map((v: { idea_id: string }) => v.idea_id));
    },
  });

  const { data: comments } = useQuery<Comment[]>({
    queryKey: ['sugg-comments', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      // CELL (M28): GET /v1/pro-hub/locations/{locationId}/idea-comments
      const { data } = isCell()
        ? { data: (await cellGet<{ rows: Comment[] }>(`/v1/pro-hub/locations/${locationId}/idea-comments`)).data?.rows ?? null }
        : await supabase.from('suggestion_comments')
        .select('id, idea_id, body, is_official, author_name, created_at').eq('location_id', locationId!)
        .order('created_at', { ascending: true });
      return (data ?? []) as Comment[];
    },
  });

  const { data: topics } = useQuery<Topic[]>({
    queryKey: ['sugg-topics', locationId],
    enabled: !!locationId,
    queryFn: async () => {
      // CELL (M28): GET /v1/pro-hub/locations/{locationId}/prompts
      const { data } = isCell()
        ? { data: (await cellGet<{ rows: Topic[] }>(`/v1/pro-hub/locations/${locationId}/prompts`)).data?.rows ?? null }
        : await supabase.from('suggestion_topics').select('id, topic, description, question_type, options')
        .eq('is_active', true).eq('location_id', locationId!).order('created_at', { ascending: false });
      return (data ?? []) as Topic[];
    },
  });

  // Which prompts THIS pro has already answered — the content-free marker (backend SUGG-PROMPT),
  // RLS-scoped to the pro's own rows. Answers are anonymous, but "did I answer this" is the pro's
  // own to know, so the question shows "answered" across reloads instead of coming back with fresh
  // buttons — and a pro cannot answer the same prompt twice.
  const { data: answeredTopics } = useQuery<Set<string>>({
    queryKey: ['sugg-answered', me?.proId, locationId],
    enabled: !!me?.proId && !!locationId,
    queryFn: async () => {
      // CELL (M28): GET /v1/pro-hub/prompts/answered (row-level security scopes it to the caller, as here)
      const { data } = isCell()
        ? { data: (await cellGet<{ rows: { topic_id: string }[] }>('/v1/pro-hub/prompts/answered')).data?.rows ?? null }
        : await supabase.from('suggestion_prompt_answered').select('topic_id');
      return new Set((data ?? []).map((r) => r.topic_id as string));
    },
  });

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('Facilities');
  const [anon, setAnon] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [commentText, setCommentText] = useState<Record<string, string>>({});

  const byIdea = new Map<string, Comment[]>();
  for (const c of comments ?? []) (byIdea.get(c.idea_id) ?? byIdea.set(c.idea_id, []).get(c.idea_id)!).push(c);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['sugg-ideas'] });
    qc.invalidateQueries({ queryKey: ['sugg-myvotes'] });
    qc.invalidateQueries({ queryKey: ['sugg-comments'] });
  };
  // Re-read the answered markers after a prompt is answered, so the question flips to "answered"
  // for real (not just in this render's memory).
  const refreshAnswered = () => qc.invalidateQueries({ queryKey: ['sugg-answered'] });

  // HUB-023: every write reads Supabase's { error } (and a thrown transport failure). A refused or
  // failed write keeps what the pro typed and says so; the input is cleared only after the row landed.
  const [formErr, setFormErr] = useState('');
  const [voteErr, setVoteErr] = useState<Record<string, string>>({});
  const [commentErr, setCommentErr] = useState<Record<string, string>>({});
  const [voting, setVoting] = useState<string | null>(null);
  const FAILED = 'That did not save — your text is still here. Try again.';

  async function submitIdea() {
    if (!title.trim() || !locationId || !me || busy) return;
    setBusy(true); setFormErr('');
    try {
      const row = {
        location_id: locationId, author_pro_id: me.proId,
        author_name: anon ? null : me.name, is_anonymous: anon,
        title: title.trim(), body: body.trim() || null, category,
      };
      // CELL (M28): POST /v1/pro-hub/locations/{locationId}/ideas (the same row; row-level security decides)
      const { error } = isCell()
        ? await cellPost(`/v1/pro-hub/locations/${locationId}/ideas`, row)
        : await supabase.from('suggestion_ideas').insert(row);
      if (error) { setFormErr(FAILED); return; }
      setTitle(''); setBody(''); setAnon(false); setShowForm(false); refresh();
    } catch {
      setFormErr(FAILED);
    } finally {
      setBusy(false);
    }
  }
  async function toggleVote(id: string) {
    if (!locationId || !me || voting) return;
    setVoting(id); setVoteErr((m) => ({ ...m, [id]: '' }));
    try {
      // CELL (M28): POST / DELETE /v1/pro-hub/locations/{locationId}/ideas/{ideaId}/vote
      const { error } = isCell()
        ? await cellCall(myVotes?.has(id) ? 'DELETE' : 'POST', `/v1/pro-hub/locations/${locationId}/ideas/${id}/vote`, { body: { voter_pro_id: me.proId } })
        : myVotes?.has(id)
        ? await supabase.from('suggestion_votes').delete().eq('idea_id', id).eq('voter_pro_id', me.proId)
        : await supabase.from('suggestion_votes').insert({ idea_id: id, location_id: locationId, voter_pro_id: me.proId });
      if (error) setVoteErr((m) => ({ ...m, [id]: 'Your vote did not save. Try again.' }));
    } catch {
      setVoteErr((m) => ({ ...m, [id]: 'Your vote did not save. Try again.' }));
    } finally {
      setVoting(null);
      refresh();
    }
  }
  // HUB-058 — one press of Post is one comment. Two halves, because a UI guard alone cannot stop
  // a retry after a lost response:
  //  1. an in-flight set (a ref, so a second click in the SAME tick sees it before any re-render)
  //     taken BEFORE the first await, plus the Post button disabled while its idea is pending;
  //  2. a client-minted row id. suggestion_comments.id is the primary key and the pro's insert may
  //     name it (pros_insert_comments checks location/author/official/idea, never id), so a retry
  //     of the SAME text re-sends the SAME id and Postgres refuses the second row with 23505 —
  //     which we read as "already posted". A different text mints a fresh id.
  const commentInFlight = useRef<Set<string>>(new Set());
  const commentAttempt = useRef<Map<string, { text: string; rowId: string }>>(new Map());
  const [commentPending, setCommentPending] = useState<Set<string>>(new Set());
  async function addComment(id: string) {
    const t = (commentText[id] ?? '').trim();
    if (!t || !locationId || !me) return;
    if (commentInFlight.current.has(id)) return;
    commentInFlight.current.add(id);
    setCommentPending((s) => new Set(s).add(id));
    setCommentErr((m) => ({ ...m, [id]: '' }));
    const prior = commentAttempt.current.get(id);
    const rowId = prior && prior.text === t ? prior.rowId : randomUUID();
    commentAttempt.current.set(id, { text: t, rowId });
    try {
      // CELL (M28): POST /v1/pro-hub/locations/{locationId}/ideas/{ideaId}/comments — a repeat answers code 23505 too
      const { error } = isCell()
        ? await cellPost(`/v1/pro-hub/locations/${locationId}/ideas/${id}/comments`, { id: rowId, author_pro_id: me.proId, author_name: me.name, is_official: false, body: t })
        : await supabase.from('suggestion_comments').insert({
        id: rowId, idea_id: id, location_id: locationId, author_pro_id: me.proId,
        author_name: me.name, is_official: false, body: t,
      });
      // 23505 on our own client-minted primary key = this exact submission already landed.
      if (error && error.code !== '23505') {
        setCommentErr((m) => ({ ...m, [id]: FAILED }));
        return;
      }
      commentAttempt.current.delete(id);
      setCommentText((m) => ({ ...m, [id]: '' })); refresh();
    } catch {
      setCommentErr((m) => ({ ...m, [id]: FAILED }));
    } finally {
      commentInFlight.current.delete(id);
      setCommentPending((s) => { const n = new Set(s); n.delete(id); return n; });
    }
  }
  const toggle = (id: string) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-image-black flex items-center gap-2">
          <span>💡</span> Suggestion <span className="text-image-pink">Box</span>
        </h1>
        {/* SAY WHERE IT GOES (Phil, testing 2026-08-08): "it's unclear where the suggestion is
            routed or who receives it." Nothing on either surface said. Named here in the pro's own
            terms — who reads it, who can vote on it, and where it stops. The owner-side copy in
            image-os-web says the same thing from the other direction; explaining only one side is
            how this got ambiguous in the first place. */}
        <p className="text-sm text-image-charcoal mt-1">
          Raise an idea, vote for the ones you want, and watch what IMAGE does about them. Your name is
          shown only if you choose to.
        </p>
        <p className="text-xs text-image-charcoal mt-2 leading-relaxed">
          <b>Where it goes:</b> everyone at your studio sees your idea and can vote on it, and your
          owner reads the same board — their reply and status show up right here. It stays inside your
          studio; no other location sees it. Below, <b>IMAGE is asking</b> holds questions posted for
          you to answer.
        </p>
      </div>

      <button onClick={() => setShowForm((v) => !v)} className="btn-primary !bg-image-pink hover:!bg-image-plum w-full">
        {showForm ? 'Cancel' : '＋ Share an idea'}
      </button>

      {showForm && (
        <div className="card !p-4 space-y-3">
          <input className="input" placeholder="Your idea, in a line…" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} />
          <textarea className="input" rows={3} placeholder="Any detail (optional)…" value={body} onChange={(e) => setBody(e.target.value)} maxLength={2000} />
          <div className="flex items-center gap-3 flex-wrap">
            <BrandSelect className="w-44" value={category} onChange={setCategory} ariaLabel="Category" options={CATEGORIES} />
            <label className="text-sm text-image-charcoal flex items-center gap-2">
              <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} /> Post anonymously
            </label>
          </div>
          {formErr && <p role="alert" className="text-xs text-red-600">{formErr}</p>}
          <button onClick={submitIdea} disabled={busy || !title.trim()} className="btn-primary !bg-image-pink w-full">
            {busy ? 'Posting…' : 'Post idea'}
          </button>
        </div>
      )}

      {isLoading && <div className="card text-center py-10 text-image-charcoal text-sm">Loading…</div>}

      {!isLoading && ideas && ideas.length === 0 && (
        <div className="card text-center py-10 text-image-charcoal text-sm">
          No ideas yet — be the first. What would make this studio better?
        </div>
      )}

      {!isLoading && ideas && ideas.map((i) => {
        const cs = (byIdea.get(i.id) ?? []);
        const official = cs.filter((c) => c.is_official).slice(-1)[0];
        const voted = myVotes?.has(i.id);
        const isOpen = expanded.has(i.id);
        const st = STATUS[i.status] ?? STATUS.open;
        return (
          <div key={i.id} className="card !p-4 flex gap-3">
            <button onClick={() => toggleVote(i.id)} disabled={voting === i.id} aria-label={voted ? 'Remove your vote' : 'Vote for this idea'}
              className={`shrink-0 w-12 rounded-lg border flex flex-col items-center justify-center py-1.5 ${voted ? 'bg-image-pink border-image-pink text-white' : 'bg-image-blush border-image-pink/30 text-image-charcoal'}`}>
              <span className="text-xs leading-none">▲</span>
              <span className="text-lg font-bold leading-tight">{i.vote_count}</span>
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {i.pinned && <span className="text-[10px]">📌</span>}
                <span className="text-sm font-semibold text-image-black">{i.title}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                {i.category && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-image-blush text-image-plum">{i.category}</span>}
              </div>
              {i.body && <p className="text-xs text-image-charcoal mt-1 leading-relaxed">{i.body}</p>}
              {voteErr[i.id] && <p role="alert" className="text-xs text-red-600 mt-1">{voteErr[i.id]}</p>}
              <div className="text-[11px] text-image-slate mt-1">
                {i.is_anonymous ? 'Anonymous' : (i.author_name ?? 'A pro')} · {new Date(i.created_at).toLocaleDateString()}
                {' · '}<button className="underline" onClick={() => toggle(i.id)}>{cs.filter((c) => !c.is_official).length} comments</button>
              </div>
              {official && (
                <div className="mt-2 bg-image-blush border-l-4 border-l-image-pink rounded-r-lg px-3 py-2 text-xs">
                  <span className="font-semibold text-image-plum">IMAGE:</span> {official.body}
                </div>
              )}
              {isOpen && (
                <div className="mt-2 border-t border-image-pink/10 pt-2 space-y-1.5">
                  {cs.filter((c) => !c.is_official).map((c) => (
                    <p key={c.id} className="text-xs text-image-charcoal">
                      <span className="font-semibold text-image-black">{c.author_name ?? 'Anonymous'}:</span> {c.body}
                    </p>
                  ))}
                  <div className="flex gap-2 pt-1">
                    <input className="input !py-1.5" placeholder="Add a comment…" value={commentText[i.id] ?? ''}
                      onChange={(e) => setCommentText((m) => ({ ...m, [i.id]: e.target.value }))} />
                    <button onClick={() => addComment(i.id)} disabled={commentPending.has(i.id)}
                      className="btn-primary !bg-image-pink !py-1.5 !px-3 text-xs">{commentPending.has(i.id) ? 'Posting…' : 'Post'}</button>
                  </div>
                  {commentErr[i.id] && <p role="alert" className="text-xs text-red-600">{commentErr[i.id]}</p>}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* IMAGE-posed prompts — kept. Pros still answer these; they live alongside the idea board. */}
      {topics && topics.length > 0 && (
        <div className="pt-2">
          <h2 className="ph-sec mb-2">IMAGE is asking</h2>
          {topics.map((t) => (
            <div key={t.id} className="card !p-4 border-l-4 border-l-image-charcoal mb-2">
              <div className="text-sm font-medium text-image-black">{t.topic}</div>
              {t.description && <p className="text-xs text-image-charcoal mt-1">{t.description}</p>}
              {answeredTopics?.has(t.id)
                ? <p className="text-xs text-emerald-600 mt-2">You answered this — thanks. Sent anonymously.</p>
                : <PromptAnswer topic={t} onDone={refreshAnswered} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A pro's answer to an IMAGE prompt — kept from the original Hub flow, anonymous per its promise.
 * The control rendered matches the prompt's question_type, and what it submits into
 * suggestion_responses.response follows THE CONTRACT exactly (shared with image-os-web + iOS):
 *   text → the free text · yes_no → "yes"/"no" · single_choice → the chosen option string · rating_1_5 → "1".."5"
 */
function PromptAnswer({ topic, onDone }: { topic: Topic; onDone: () => void }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  const qt = topic.question_type || 'text';

  async function submit(response: string) {
    const v = response.trim();
    if (!v || busy) return;
    setBusy(true); setErr('');
    // THROUGH answer_prompt (backend SUGG-PROMPT), never a raw insert: it writes the ANONYMOUS
    // response and the content-free "answered" marker in one transaction, so a pro can answer a
    // prompt only once and the owner's roll-up is never inflated by repeat taps.
    // CELL (M28): POST /v1/pro-hub/prompts/{topicId}/answer (answer_prompt, unchanged)
    const { data: result, error } = isCell()
      ? await cellPost<{ result: string }>(`/v1/pro-hub/prompts/${topic.id}/answer`, { response: v }).then((r) => ({ data: r.data?.result ?? null, error: r.error }))
      : await supabase.rpc('answer_prompt', { p_topic_id: topic.id, p_response: v });
    setBusy(false);
    if (error) { setErr('Could not send that — try again.'); return; }
    // 'ok' (recorded) and 'already' (answered before) both mean the question is done for this pro.
    if (result === 'ok' || result === 'already') { setText(''); setSent(true); onDone(); }
    else setErr('Could not send that — try again.');
  }

  if (sent) return <p className="text-xs text-emerald-600 mt-2">Thanks — sent anonymously.</p>;

  // The control matches the prompt's question_type; the error line is appended once below so no
  // mode can silently swallow a failed send.
  let control: JSX.Element;
  if (qt === 'yes_no') {
    control = (
      <div className="flex gap-2 mt-2">
        {['yes', 'no'].map((v) => (
          <button key={v} disabled={busy} onClick={() => submit(v)}
            className="btn-primary !bg-image-charcoal !py-1.5 !px-5 text-xs capitalize">{v}</button>
        ))}
      </div>
    );
  } else if (qt === 'single_choice') {
    const opts = topic.options ?? [];
    control = (
      <div className="flex flex-wrap gap-2 mt-2">
        {opts.map((o) => (
          <button key={o} disabled={busy} onClick={() => submit(o)}
            className="rounded-full border border-image-pink/40 bg-image-blush text-image-plum text-xs px-3 py-1.5 hover:bg-image-pink hover:text-white transition-colors">
            {o}
          </button>
        ))}
      </div>
    );
  } else if (qt === 'rating_1_5') {
    control = (
      <div className="flex items-center gap-1.5 mt-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} disabled={busy} onClick={() => submit(String(n))}
            className="w-8 h-8 rounded-lg border border-image-pink/40 bg-image-blush text-image-charcoal text-sm font-semibold hover:bg-image-pink hover:text-white transition-colors">
            {n}
          </button>
        ))}
        <span className="text-[11px] text-image-slate ml-1">1–5</span>
      </div>
    );
  } else {
    // text (default)
    control = (
      <div className="flex gap-2 mt-2">
        <input className="input !py-1.5" placeholder="Your answer (anonymous)…" value={text} onChange={(e) => setText(e.target.value)} />
        <button disabled={busy} onClick={() => submit(text)} className="btn-primary !bg-image-charcoal !py-1.5 !px-3 text-xs">Send</button>
      </div>
    );
  }

  return (
    <>
      {control}
      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
    </>
  );
}
